import React from 'react';
import {
  Divider,
  Grid,
  Typography,
  makeStyles,
  Tooltip,
  Box,
  Hidden,
  Button,
  Container,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import HelpIcon from '@material-ui/icons/HelpOutline';
import PageLayout from '../components/page-layout';
import Seo from '../components/seo';
import Section from '../components/section';
import { StyledCard } from '../components/styled-card';
import Link from '../components/link';

type FeatureRowProps = React.PropsWithChildren<{
  enabled?: boolean;
  hint?: string;
}>;

const featureRowStyles = makeStyles(theme => ({
  row: {
    padding: theme.spacing(2),
    textAlign: 'left',
  },
  checkIcon: (props: FeatureRowProps) => ({
    color: !props.enabled ? 'rgba(0, 0, 0, 0.1)' : theme.palette.primary.main,
    marginRight: theme.spacing(2),
  }),
}));

const FeatureRow: React.FC<FeatureRowProps> = (props: FeatureRowProps) => {
  const classes = featureRowStyles(props);

  return (
    <Box display="flex" alignItems="center" className={classes.row}>
      <CheckIcon className={classes.checkIcon} />
      <Box flexGrow={1}>{props.children}</Box>
      <Hidden mdDown>
        {props.hint ? (
          <Tooltip title={props.hint}>
            <HelpIcon />
          </Tooltip>
        ) : (
          ''
        )}
      </Hidden>
    </Box>
  );
};

const pricingStyles = makeStyles(theme => ({
  row: {
    padding: theme.spacing(2),
  },
  bold: {
    fontWeight: 700,
  },
}));

const PricingPage: React.FC = () => {
  const classes = pricingStyles();

  return (
    <PageLayout>
      <Seo title="Pricing" />

      <Section variant="hero-home">
        <Typography variant="h1" align="center" paragraph>
          Pricing
        </Typography>
        <Typography variant="subtitle1" align="center">
          Want to automate DevOps with Architect? Our team is here to help you get started!
        </Typography>
      </Section>

      <Section maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item md={4} xs={12}>
            <StyledCard padding="0">
              <Typography variant="h6" align="center" className={`${classes.row} ${classes.bold}`}>
                Sandbox
              </Typography>

              <Divider />

              <div className={classes.row}>
                <Typography variant="h6" align="center" className={classes.bold}>
                  FREE
                </Typography>
                <Typography variant="body2" align="center">
                  Great for trials, hobbyists, and students
                </Typography>
              </div>

              <Divider />

              <div className={classes.row}>
                <Typography variant="body2" align="center">
                  1 environment
                </Typography>
                <Typography variant="body2" align="center">
                  15 services/environment
                </Typography>
              </div>

              <Divider />

              <FeatureRow
                enabled
                hint="A central registry of components that can be extended and deployed anywhere"
              >
                Service catalog
              </FeatureRow>

              <Divider />

              <FeatureRow enabled hint="There's no limit to the number of deployments you can make">
                Recursive deployments
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically enrich consuming applications with the address of downstream services"
              >
                Dependency injection
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically undo any deployment with the push of a button"
              >
                Push-button rollbacks
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Generate secure routes with a Traefik API gateway for north-south traffic"
              >
                Automated gateway configuration
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Register services automatically with a Consul service mesh for east-west traffic"
              >
                Automated mesh configuration
              </FeatureRow>

              <Divider />

              <FeatureRow enabled hint="History of executed deployments, state, and resource diff">
                Deployment audit trail
              </FeatureRow>

              <Divider />

              <FeatureRow hint="Provision multiple instances of the same service for data segmentation">
                Multi-tenancy
              </FeatureRow>

              <Divider />

              <FeatureRow hint="Automatically generate Consul intentions to lock down traffic between dependent services">
                Zero-trust network policies
              </FeatureRow>

              <Divider />

              <FeatureRow hint="Protect your production environment without creating friction for engineers early in the development lifecycle">
                Approval-gated environments
              </FeatureRow>

              <Divider />

              <div className={classes.row}>
                <Button
                  href="//cloud.architect.io/signup"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  Sign up
                </Button>
              </div>
            </StyledCard>
          </Grid>

          <Grid item md={4} xs={12}>
            <StyledCard padding="0">
              <Typography variant="h6" align="center" className={`${classes.row} ${classes.bold}`}>
                Professional
              </Typography>

              <Divider />

              <div className={classes.row}>
                <Typography variant="h6" align="center" className={classes.bold}>
                  CONTACT US
                </Typography>
                <Typography variant="body2" align="center">
                  Automate DevOps for your whole team
                </Typography>
              </div>

              <Divider />

              <div className={classes.row}>
                <Typography variant="body2" align="center">
                  15 environments
                </Typography>
                <Typography variant="body2" align="center">
                  15 services/environment
                </Typography>
              </div>

              <Divider />

              <FeatureRow
                enabled
                hint="A central registry of components that can be extended and deployed anywhere"
              >
                Service catalog
              </FeatureRow>

              <Divider />

              <FeatureRow enabled hint="There's no limit to the number of deployments you can make">
                Recursive deployments
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically enrich consuming applications with the address of downstream services"
              >
                Dependency injection
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically undo any deployment with the push of a button"
              >
                Push-button rollbacks
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Generate secure routes with a Traefik API gateway for north-south traffic"
              >
                Automated gateway configuration
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Register services automatically with a Consul service mesh for east-west traffic"
              >
                Automated mesh configuration
              </FeatureRow>

              <Divider />

              <FeatureRow enabled hint="History of executed deployments, state, and resource diff">
                Deployment audit trail
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Provision multiple instances of the same service for data segmentation"
              >
                Multi-tenancy
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically generate Consul intentions to lock down traffic between dependent services"
              >
                Zero-trust network policies
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Protect your production environment without creating friction for engineers early in the development lifecycle"
              >
                Approval-gated environments
              </FeatureRow>

              <Divider />

              <div className={classes.row}>
                <Link to="/contact-us/">
                  <Button variant="outlined" color="primary" size="large">
                    Contact us
                  </Button>
                </Link>
              </div>
            </StyledCard>
          </Grid>

          <Grid item md={4} xs={12}>
            <StyledCard padding="0">
              <Typography variant="h6" align="center" className={`${classes.row} ${classes.bold}`}>
                On-Prem
              </Typography>

              <Divider />

              <div className={classes.row}>
                <Typography variant="h6" align="center" className={classes.bold}>
                  CONTACT US
                </Typography>
                <Typography variant="body2" align="center">
                  Serve your entire org from inside your firewall
                </Typography>
              </div>

              <Divider />

              <div className={classes.row}>
                <Typography variant="body2" align="center">
                  Unlimited environments
                </Typography>
                <Typography variant="body2" align="center">
                  Unlimited services/environment
                </Typography>
              </div>

              <Divider />

              <FeatureRow
                enabled
                hint="A central registry of components that can be extended and deployed anywhere"
              >
                Service catalog
              </FeatureRow>

              <Divider />

              <FeatureRow enabled hint="There's no limit to the number of deployments you can make">
                Recursive deployments
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically enrich consuming applications with the address of downstream services"
              >
                Dependency injection
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically undo any deployment with the push of a button"
              >
                Push-button rollbacks
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Generate secure routes with a Traefik API gateway for north-south traffic"
              >
                Automated gateway configuration
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Register services automatically with a Consul service mesh for east-west traffic"
              >
                Automated mesh configuration
              </FeatureRow>

              <Divider />

              <FeatureRow enabled hint="History of executed deployments, state, and resource diff">
                Deployment audit trail
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Provision multiple instances of the same service for data segmentation"
              >
                Multi-tenancy
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Automatically generate Consul intentions to lock down traffic between dependent services"
              >
                Zero-trust network policies
              </FeatureRow>

              <Divider />

              <FeatureRow
                enabled
                hint="Protect your production environment without creating friction for engineers early in the development lifecycle"
              >
                Approval-gated environments
              </FeatureRow>

              <Divider />

              <div className={classes.row}>
                <Link to="/contact-us/">
                  <Button variant="outlined" color="primary" size="large">
                    Contact us
                  </Button>
                </Link>
              </div>
            </StyledCard>
          </Grid>
        </Grid>
      </Section>

      <Container>
        <Divider />
      </Container>

      <Section maxWidth="lg">
        <Typography variant="h3" paragraph>
          DevOps &amp; digital transformation services
        </Typography>
        <Typography variant="subtitle1" paragraph>
          Worried your existing team or application isn't ready to reap the benefits of Architect
          Cloud? Our talented team of engineers have got you covered! We offer a full suite of
          services to help transform your application for the next generation of cloud-native tools,
          technology, and processes.
        </Typography>

        <Link to="/contact-us/">
          <Button variant="outlined" color="primary" size="large">
            Learn more about digital transformation services
          </Button>
        </Link>
      </Section>
    </PageLayout>
  );
};

export default PricingPage;
